// Live input module — Web MIDI + computer keyboard route into a "selected"
// pitched instrument. Emits a small status panel under the channel rack so
// the user can pick the destination and see device state.
//
// Browser support:
//   - Web MIDI: Chrome / Edge (also Safari behind a flag); requires user
//     permission on first use.
//   - Computer keyboard: works everywhere; A W S E D F T G Y H U J K maps to
//     C C# D D# E F F# G G# A A# B C+1 of the live octave; Z / X shift octave.

import { h, clear } from '../ui/dom.js';

const COMPUTER_KEYS = {
    a: 'C', w: 'C#', s: 'D', e: 'D#', d: 'E', f: 'F',
    t: 'F#', g: 'G', y: 'G#', h: 'A', u: 'A#', j: 'B', k: 'C+1',
};

export default {
    id: 'input-midi',
    name: 'Live input',
    register(ctx) {
        let octave = 4;
        let target = 'piano';
        const held = new Set();
        let midiAccess = null;
        let midiStatus = 'unrequested';

        // Mount a panel into the app once UI is up.
        const panelSlot = h('section.lane.input-lane');
        document.querySelector('.app').insertBefore(panelSlot, document.querySelector('.lane.mixer-lane'));

        function render() {
            clear(panelSlot);
            const targetSel = h('select', {
                value: target,
                onChange: (e) => { target = e.target.value; },
            });
            for (const inst of ctx.instruments.all().filter(i => i.kind === 'pitched')) {
                targetSel.appendChild(h('option', { value: inst.id, selected: inst.id === target }, inst.name));
            }
            const octSel = h('select', {
                value: String(octave),
                onChange: (e) => { octave = parseInt(e.target.value, 10); },
            });
            for (let o = 1; o <= 7; o++) {
                octSel.appendChild(h('option', { value: String(o), selected: o === octave }, String(o)));
            }
            const midiBtn = h('button.btn', {
                onClick: requestMidi,
                disabled: midiStatus === 'connected',
            }, midiButtonLabel());
            const status = h('span.input-status', {}, statusText());
            panelSlot.appendChild(h('div.lane-header', {}, [
                h('h2', {}, 'Live input'),
                h('div.lane-controls', {}, [
                    h('label', {}, ['Target', targetSel]),
                    h('label', {}, ['Octave', octSel]),
                    midiBtn,
                    status,
                    h('span.hint', {}, 'A W S E D F T G Y H U J K · Z/X shift octave'),
                ]),
            ]));
        }

        function midiButtonLabel() {
            if (midiStatus === 'unrequested') return 'Connect MIDI';
            if (midiStatus === 'requesting') return 'Connecting…';
            if (midiStatus === 'denied') return 'MIDI denied';
            if (midiStatus === 'unsupported') return 'MIDI unsupported';
            return 'MIDI connected';
        }

        function statusText() {
            if (midiStatus === 'connected' && midiAccess) {
                const inputs = Array.from(midiAccess.inputs.values()).map(i => i.name).join(', ');
                return inputs ? `· ${inputs}` : '· no devices';
            }
            return '';
        }

        async function requestMidi() {
            if (!navigator.requestMIDIAccess) { midiStatus = 'unsupported'; render(); return; }
            midiStatus = 'requesting'; render();
            try {
                midiAccess = await navigator.requestMIDIAccess({ sysex: false });
                midiStatus = 'connected';
                attachMidi(midiAccess);
                midiAccess.onstatechange = () => render();
                render();
            } catch (e) {
                midiStatus = 'denied'; render();
            }
        }

        function attachMidi(access) {
            for (const input of access.inputs.values()) {
                input.onmidimessage = onMidiMessage;
            }
        }

        function onMidiMessage(msg) {
            const [status, note, velocity] = msg.data;
            const cmd = status >> 4;
            // 9 = note on, 8 = note off (note-on with velocity 0 also = note-off)
            if (cmd === 9 && velocity > 0) {
                const pitch = ctx.Tone.Frequency(note, 'midi').toNote();
                playNow(pitch, velocity / 127);
            }
        }

        function resolveLetter(letter) {
            if (letter === 'C+1') return `C${octave + 1}`;
            return `${letter}${octave}`;
        }

        function playNow(pitch, vel = 0.85) {
            const inst = ctx.instruments.get(target);
            if (!inst) return;
            inst.play(pitch, ctx.Tone.now(), '4n', vel);
        }

        document.addEventListener('keydown', (e) => {
            if (e.repeat) return;
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
            const k = e.key.toLowerCase();
            if (k === 'z') { octave = Math.max(1, octave - 1); render(); return; }
            if (k === 'x') { octave = Math.min(7, octave + 1); render(); return; }
            const letter = COMPUTER_KEYS[k];
            if (!letter || held.has(k)) return;
            held.add(k);
            playNow(resolveLetter(letter));
        });
        document.addEventListener('keyup', (e) => held.delete(e.key.toLowerCase()));

        render();
        ctx.bus.on('instrument:added', render);
    },
};
